import { getCellByPipelineId, isRecordsCell } from '@/core/cell';

import { generateShortId } from '@/lib/utils/string';

import {
  Exploration,
  PipelineOperation,
  CompositeFilterCondition,
  Pipeline,
  Visualisation,
  RecordsCell,
} from '../../types';
import { isKeyedAggregation, isValueExpression } from '../../pipeline/operation';
import { flattenPipeline } from '../../pipeline/utils';

export const convertJoinPipelineParametersToSteps = (exploration: Exploration): Exploration => {
  return {
    ...exploration,
    view: {
      ...exploration.view,
      cells: exploration.view.cells.map((cell) => {
        if (!isRecordsCell(cell)) {
          return cell;
        }
        const operation = cell.pipeline.operations.at(0);
        if (operation?.operation !== 'joinPipeline') {
          return cell;
        }
        if ('steps' in operation.parameters) {
          return cell;
        }
        return {
          ...cell,
          pipeline: {
            ...cell.pipeline,
            operations: [
              {
                ...operation,
                parameters: {
                  steps: [operation.parameters],
                },
              },
              ...cell.pipeline.operations.slice(1),
            ],
          },
        };
      }),
    },
  };
};

const getStepId = (
  operationIndex: number,
  stepIndex: number,
  pipeline: Pipeline,
): string | undefined => {
  const operation = pipeline.operations.at(operationIndex);
  if (operation?.operation === 'joinPipeline' && 'steps' in operation.parameters) {
    const step = operation.parameters.steps.at(stepIndex);
    return step?.stepId;
  }
  return undefined;
};

const getFullPipelineById = (
  pipelineId: string,
  exploration: Exploration,
): Pipeline | undefined => {
  const cell = getCellByPipelineId(exploration.view.cells, pipelineId);
  if (!cell) {
    return undefined;
  }
  return flattenPipeline(cell.pipeline, exploration);
};

const replaceKey = (key: string, fullPipeline: Pipeline | undefined): string => {
  if (!fullPipeline) {
    return key;
  }
  const matches = key.match(/^join_(\d+)(_(\d+))?_r_/);
  if (matches) {
    const stepId = getStepId(
      parseInt(matches[1], 10),
      parseInt(matches[3] ?? '0', 10),
      fullPipeline,
    );
    if (stepId !== undefined) {
      return `${key.slice(matches[0].length)}_${stepId}`;
    }
  }
  return key;
};

const replaceInValue = <T>(value: T, fullPipeline: Pipeline) => {
  if (isValueExpression(value)) {
    const updatedExpression = value.expression.replace(
      /join_(\d+)(_(\d+))?_r_([a-zA-Z0-9_]+)/g,
      (_match, p1, _, p3, p4) => {
        const stepId = getStepId(parseInt(p1, 10), parseInt(p3 ?? '0', 10), fullPipeline);
        if (stepId !== undefined) {
          return `${p4}_${stepId}`;
        }
        return _match;
      },
    );
    return {
      ...value,
      expression: updatedExpression,
    };
  }
  return value;
};

const replaceKeysInCompositeFilterCondition = (
  condition: CompositeFilterCondition,
  fullPipeline: Pipeline | undefined,
): CompositeFilterCondition => {
  if (fullPipeline === undefined) {
    return condition;
  }
  if ('operands' in condition) {
    return {
      ...condition,
      operands: condition.operands.map((operand) =>
        replaceKeysInCompositeFilterCondition(operand, fullPipeline),
      ),
    };
  }
  return {
    ...condition,
    key: replaceKey(condition.key, fullPipeline),
    value: replaceInValue(condition.value, fullPipeline),
  };
};

const replaceKeysInOperation = (
  operation: PipelineOperation,
  pipeline: Pipeline,
  exploration: Exploration,
) => {
  const currentPipeline = flattenPipeline(pipeline, exploration);

  switch (operation.operation) {
    case 'filter':
      return {
        ...operation,
        parameters: replaceKeysInCompositeFilterCondition(operation.parameters, currentPipeline),
      };

    case 'groupAggregate':
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          groups: operation.parameters.groups?.map((group) => ({
            ...group,
            key: replaceKey(group.key, currentPipeline),
          })),
          aggregations: operation.parameters.aggregations?.map((agg) => {
            if (!isKeyedAggregation(agg)) {
              return agg;
            }
            return {
              ...agg,
              key: replaceKey(agg.key, currentPipeline),
              ...('sort' in agg && agg.sort !== undefined
                ? {
                    sort: agg.sort.map((sort) => ({
                      ...sort,
                      key: replaceKey(sort.key, currentPipeline),
                    })),
                  }
                : {}),
            };
          }),
          slice:
            operation.parameters.slice !== undefined
              ? {
                  ...operation.parameters.slice,
                  sort: operation.parameters.slice.sort?.map((sort) => ({
                    ...sort,
                    key: replaceKey(sort.key, currentPipeline),
                  })),
                }
              : operation.parameters.slice,
        },
      };

    case 'deriveField':
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          value: replaceInValue(operation.parameters.value, currentPipeline),
        },
      };

    case 'relationAggregate': {
      const relatedPipelineId = operation.parameters.pipelineId;
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          aggregations:
            relatedPipelineId !== undefined
              ? operation.parameters.aggregations?.map((agg) => {
                  if (!isKeyedAggregation(agg)) {
                    return agg;
                  }
                  return {
                    ...agg,
                    key: replaceKey(agg.key, getFullPipelineById(relatedPipelineId, exploration)),
                  };
                })
              : operation.parameters.aggregations,
          ...('joinStrategy' in operation.parameters &&
          operation.parameters.joinStrategy !== undefined
            ? {
                joinStrategy: {
                  ...operation.parameters.joinStrategy,
                  joinKeyOnBase: replaceKey(
                    operation.parameters.joinStrategy.joinKeyOnBase,
                    currentPipeline,
                  ),
                  joinKeyOnRelated: replaceKey(
                    operation.parameters.joinStrategy.joinKeyOnRelated,
                    getFullPipelineById(operation.parameters.pipelineId, exploration),
                  ),
                },
              }
            : {}),
          ...(relatedPipelineId !== undefined &&
          'filter' in operation.parameters &&
          operation.parameters.filter !== undefined
            ? {
                filter: replaceKeysInCompositeFilterCondition(
                  operation.parameters.filter,
                  getFullPipelineById(relatedPipelineId, exploration),
                ),
              }
            : {}),
        },
      };
    }
    case 'addRelatedColumn': {
      const relatedPipelineId = operation.parameters.pipelineId;
      if (relatedPipelineId === undefined) {
        return operation;
      }
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          columns: operation.parameters.columns.map((col) => ({
            ...col,
            key: replaceKey(col.key, getFullPipelineById(relatedPipelineId, exploration)),
          })),
        },
      };
    }

    default:
      return operation;
  }
};

const replaceKeysInPipeline = (pipeline: Pipeline, exploration: Exploration) => {
  return {
    ...pipeline,
    operations: pipeline.operations.map((operation: any) =>
      replaceKeysInOperation(operation, pipeline, exploration),
    ),
  };
};

const replaceKeysInVisualisations = (cell: RecordsCell, exploration: Exploration): RecordsCell => {
  if (!cell.visualisations || cell.visualisations.length === 0) {
    return cell;
  }

  const currentPipeline = flattenPipeline(cell.pipeline, exploration);

  const updatedVisualisations = cell.visualisations.map((visualisation: Visualisation) => {
    const updatedVisualisation = {
      ...visualisation,
      valueKeys: visualisation.valueKeys.map((key) => replaceKey(key, currentPipeline)),
      mainAxisKey:
        visualisation.mainAxisKey !== undefined && visualisation.mainAxisKey !== null
          ? replaceKey(visualisation.mainAxisKey, currentPipeline)
          : visualisation.mainAxisKey,
    };

    if (visualisation.viewOptions) {
      const updatedViewOptions = {
        ...visualisation.viewOptions,
      };

      // Update axes keys
      if (visualisation.viewOptions.axes?.right?.keys) {
        updatedViewOptions.axes = {
          ...visualisation.viewOptions.axes,
          right: {
            ...visualisation.viewOptions.axes.right,
            keys: visualisation.viewOptions.axes.right.keys.map((key) =>
              replaceKey(key, currentPipeline),
            ),
          },
        };
      }

      // Update series keys
      if (visualisation.viewOptions.series) {
        const updatedSeries: Record<string, any> = {};
        Object.entries(visualisation.viewOptions.series).forEach(([key, value]) => {
          const newKey = replaceKey(key, currentPipeline);
          updatedSeries[newKey] = value;
        });
        updatedViewOptions.series = updatedSeries;
      }

      updatedVisualisation.viewOptions = updatedViewOptions;
    }

    return updatedVisualisation;
  });

  return {
    ...cell,
    visualisations: updatedVisualisations,
  };
};

const replaceKeysInSort = (cell: RecordsCell, exploration: Exploration): RecordsCell => {
  if (!cell.sort || cell.sort.length === 0) {
    return cell;
  }

  const currentPipeline = flattenPipeline(cell.pipeline, exploration);

  const updatedSort = cell.sort.map((sortItem) => ({
    ...sortItem,
    key: replaceKey(sortItem.key, currentPipeline),
  }));

  return {
    ...cell,
    sort: updatedSort,
  };
};

const replaceKeysInExcludeProperties = (
  cell: RecordsCell,
  exploration: Exploration,
): RecordsCell => {
  if (!cell.excludeProperties || cell.excludeProperties.length === 0) {
    return cell;
  }

  const currentPipeline = flattenPipeline(cell.pipeline, exploration);

  const updatedExcludeProperties = cell.excludeProperties.map((key) =>
    replaceKey(key, currentPipeline),
  );

  return {
    ...cell,
    excludeProperties: updatedExcludeProperties,
  };
};

export const addJoinPipelineStepIds = (exploration: Exploration): Exploration => {
  const updatedExploration = {
    ...exploration,
    view: {
      ...exploration.view,
      cells: exploration.view.cells.map((cell) => {
        if (!isRecordsCell(cell)) {
          return cell;
        }

        const updatedOperations = cell.pipeline.operations.map((operation) => {
          if (operation.operation !== 'joinPipeline') {
            return operation;
          }
          if (!('steps' in operation.parameters)) {
            return operation;
          }

          const updatedSteps = operation.parameters.steps.map((step) => {
            return {
              ...step,
              stepId: step.stepId ?? generateShortId(),
            };
          });

          return {
            ...operation,
            parameters: {
              ...operation.parameters,
              steps: updatedSteps,
            },
          };
        });

        return {
          ...cell,
          pipeline: {
            ...cell.pipeline,
            operations: updatedOperations,
          },
        };
      }),
    },
  };

  const finalCells = updatedExploration.view.cells.map((cell) => {
    if (!isRecordsCell(cell)) {
      return cell;
    }

    return replaceKeysInVisualisations(
      replaceKeysInSort(
        replaceKeysInExcludeProperties(
          {
            ...cell,
            pipeline: replaceKeysInPipeline(cell.pipeline, updatedExploration),
          },
          updatedExploration,
        ),
        updatedExploration,
      ),
      updatedExploration,
    );
  });

  return {
    ...exploration,
    view: {
      ...exploration.view,
      cells: finalCells,
    },
  };
};
